import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { CREATE_SUPPORT_TICKET } from '../../graphql/support';
import GButton from '../../components/ui/base/GButton';
import type { SupportTicket } from './index';

interface CreateTicketFormProps {
    onSuccess: (ticket: SupportTicket) => void;
    onCancel: () => void;
}

const CATEGORIES = [
    { value: 'GENERAL', label: 'General' },
    { value: 'AUTHENTICATION', label: 'Authentication' },
    { value: 'PAYMENT', label: 'Payment' },
    { value: 'DEPOSIT', label: 'Deposit' },
    { value: 'WITHDRAWAL', label: 'Withdrawal' },
    { value: 'BONUS', label: 'Bonus' },
    { value: 'GAMES', label: 'Games' },
    { value: 'ACCOUNT', label: 'Account' },
    { value: 'TECHNICAL', label: 'Technical' },
    { value: 'OTHER', label: 'Other' },
];

const CreateTicketForm: React.FC<CreateTicketFormProps> = ({ onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('GENERAL');
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const [createTicket, { loading }] = useMutation(CREATE_SUPPORT_TICKET, { errorPolicy: 'all' });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !description.trim()) {
            setMessage({ type: 'error', text: t('support.fillAllFields') });
            return;
        }
        setMessage(null);
        try {
            const result = await createTicket({
                variables: {
                    input: {
                        title: title.trim(),
                        description: description.trim(),
                        category,
                    },
                },
            });
            if (result.errors && result.errors.length > 0) {
                setMessage({ type: 'error', text: result.errors[0].message });
            } else if (result.data?.createSupportTicket) {
                onSuccess(result.data.createSupportTicket);
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err?.message || t('support.createFailed') });
        }
    };

    return (
        <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] p-[24px]">
            <h3 className="text-white text-[16px] font-bold mb-[20px]">{t('support.createTicket')}</h3>

            <form onSubmit={handleSubmit} className="space-y-[20px]">
                {/* Title */}
                <div>
                    <label className="block text-white/40 text-[12px] font-semibold uppercase tracking-widest mb-[8px]">
                        {t('support.ticketTitle')}
                    </label>
                    <input
                        type="text"
                        placeholder={t('support.titlePlaceholder')}
                        value={title}
                        onChange={(e) => { setTitle(e.target.value); setMessage(null); }}
                        className="w-full px-[16px] py-[12px] rounded-[12px] bg-white/[0.06] border border-white/[0.1] text-white text-[15px] font-medium outline-none placeholder-white/20 focus:border-[#4DBDFF]/50 focus:shadow-[0_0_15px_rgba(0,134,211,0.1)] transition-all duration-300"
                        required
                    />
                </div>

                {/* Category */}
                <div>
                    <label className="block text-white/40 text-[12px] font-semibold uppercase tracking-widest mb-[8px]">
                        {t('support.category')}
                    </label>
                    <div className="flex flex-wrap gap-[6px]">
                        {CATEGORIES.map((cat) => (
                            <button
                                key={cat.value}
                                type="button"
                                onClick={() => setCategory(cat.value)}
                                className={`px-[12px] py-[6px] rounded-full text-[12px] font-semibold border transition-all duration-200 ${
                                    category === cat.value
                                        ? 'bg-gradient-to-r from-[#4DBDFF]/20 to-[#0086D3]/20 border-[#4DBDFF]/30 text-[#4DBDFF]'
                                        : 'bg-white/[0.04] border-white/[0.06] text-white/40 hover:border-white/[0.12] hover:text-white/60'
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-white/40 text-[12px] font-semibold uppercase tracking-widest mb-[8px]">
                        {t('support.description')}
                    </label>
                    <textarea
                        placeholder={t('support.descriptionPlaceholder')}
                        value={description}
                        onChange={(e) => { setDescription(e.target.value); setMessage(null); }}
                        rows={5}
                        className="w-full px-[16px] py-[12px] rounded-[12px] bg-white/[0.06] border border-white/[0.1] text-white text-[15px] font-medium outline-none placeholder-white/20 focus:border-[#4DBDFF]/50 focus:shadow-[0_0_15px_rgba(0,134,211,0.1)] transition-all duration-300 resize-none"
                        required
                    />
                </div>

                {/* Message */}
                {message && (
                    <div className={`px-4 py-3 rounded-[12px] text-[14px] font-semibold animate-[fadeIn_0.3s_ease-out] ${
                        message.type === 'success'
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                        {message.text}
                    </div>
                )}

                {/* Buttons */}
                <div className="flex gap-[10px] pt-[4px]">
                    <GButton
                        variant="primary"
                        className="py-[12px] px-[24px] rounded-[12px]"
                        disabled={loading || !title.trim() || !description.trim()}
                        type="submit"
                    >
                        {loading ? t('support.submitting') : t('support.submit')}
                    </GButton>
                    <GButton
                        variant="secondary"
                        className="py-[12px] px-[24px] rounded-[12px]"
                        onClick={onCancel}
                        type="button"
                    >
                        {t('common.cancel')}
                    </GButton>
                </div>
            </form>
        </div>
    );
};

export default CreateTicketForm;
